/**
 * /api/cards/:cardId/sessions + /api/sessions/:id — v6 lifecycle endpoints.
 *
 * Wraps the chaos-v6-gateway Hono routes implemented in
 * apps/chaos-gateway/src/sessions.ts. These are the Phase B endpoints
 * that actually spawn Docker containers and proxy a tmux pty over WS.
 */
import { apiJson } from './client';

export type AgentType = 'claude' | 'codex' | 'gemini';

export type V6SessionState = 'created' | 'waking' | 'active' | 'sleeping' | 'dead';

export type V6SessionMode = 'cli' | 'chat';

/**
 * M-X8 (2026-05-22): which CLI vendor a session uses for its chat
 * messages. 'anthropic' is the legacy default; new vendors are
 * exposed via the UI vendor picker.
 */
export type Vendor = 'anthropic' | 'openai' | 'google';

/**
 * M-X16 (2026-05-22): per-session reasoning-effort knob shown alongside
 * vendor + model in the chat composer toolbar.
 *
 * 2026-05-25: expanded to the verified vendor-CLI vocabulary:
 *   anthropic claude → low | medium | high | xhigh | max
 *   openai   codex   → none | minimal | low | medium | high | xhigh
 *   google   gemini  → none | low | medium | high | auto  (thinkingLevel)
 * Union below covers all three; adapters map values to vendor flags.
 */
export type Effort =
  | 'none'
  | 'minimal'
  | 'low'
  | 'medium'
  | 'high'
  | 'xhigh'
  | 'max'
  | 'auto'
  | 'ultracode'
  // 2026-10-04: OpenAI's own top level (codex 0.160.0 model/list).
  | 'ultra';

/**
 * M-X26 (2026-05-22): per-session orchestration preference shown as a
 * segmented control under the chat composer toolbar.
 *
 *   'auto'        — default; no extra cascade hint, agent decides as
 *                   usual (org constitution governs).
 *   'orchestrate' — soft hint: prefer delegating to sub-agents.
 *   'direct'      — soft hint: prefer doing the work inline.
 */
export type SubagentPreference = 'auto' | 'orchestrate' | 'direct';

export interface V6Session {
  id: string;
  cardId: string;
  /** Latest measured context level from GET /sessions/:id; absent in lists. */
  context?: { tokens: number; window: number | null; runId: string | null } | null;
  /**
   * WP-4a (actor/executor guard): the person whose AI subscription this
   * session runs on. Present only while the gateway's
   * SESSION_ACTOR_EXECUTOR_GUARD_ENABLED is on — absent means "not known", and
   * every rule that reads it then behaves exactly as before. Null on an
   * unsealed legacy row.
   */
  executingUserId?: string | null;
  /**
   * Org this session belongs to. The card role-picker fetches its role list
   * for THIS org so it only offers roles addable to the card — the gateway
   * validates POST .../roles against the session org (role_cross_org check).
   * Nullable on legacy sessions that pre-date the org-architecture work.
   */
  orgId?: string | null;
  sessionNumber: number;
  state: V6SessionState;
  agentType: AgentType;
  mode?: V6SessionMode;
  /**
   * M-X8 (2026-05-22): currently-selected vendor for new chat messages.
   * M-X16 (2026-05-22): may be null on fresh sessions until either the
   * founder picks via PATCH or a role with default_vendor joins.
   */
  vendor?: Vendor | null;
  /** M-X16: persisted model id (free-form, mirrors MODEL_PRESETS_BY_VENDOR). */
  model?: string | null;
  /** M-X16: persisted reasoning effort. */
  effort?: Effort | null;
  /**
   * 0393 (founder 2026-10-04): where model / effort came from — 'manual' (picked
   * in the picker; role defaults never override it), 'role' (preselected from
   * the attached roles' most advanced default), 'system' (the system default).
   */
  modelSource?: 'system' | 'role' | 'manual';
  effortSource?: 'system' | 'role' | 'manual';
  /**
   * M-X26: persisted orchestration mode. NOT NULL on the DB row
   * (default 'auto'); legacy responses without this field are coerced
   * to 'auto' in the gateway and the chat pane.
   */
  subagentPreference?: SubagentPreference;
  /**
   * 2026-05-25 (founder spec): mirrors Claude CLI's --fast-mode flag.
   * Persisted on chaos.sessions.fast_mode (default false). Only the
   * anthropic adapter wires the CLI flag; other adapters ignore it.
   */
  fastMode?: boolean;
  /**
   * 2026-07-28: how many chat messages this session holds, from
   * GET /cards/:id/sessions. `undefined` means the gateway could not count
   * (old build, or the rollup query failed) — it does NOT mean zero, and
   * pickSessionForCard() falls back to the newest-first rule when it is absent
   * rather than treating a real conversation as empty.
   */
  messageCount?: number;
  /**
   * 2026-07-28: 'interactive' (tmux, the only correct value for a card agent
   * session) or 'print' (a crippled legacy/imported session — no ask_human,
   * no sub-agents, no chaos.* MCP). Surfaced so the pane can SAY so instead of
   * letting a degraded session look normal. Migration 0160 makes 'print'
   * unreachable for newly created card chat sessions.
   */
  chatBackend?: 'interactive' | 'print' | null;
  containerId?: string | null;
  cliSessionId?: string | null;
  createdAt?: string | null;
  startedAt?: string | null;
  sleepAt?: string | null;
  lastWokenAt?: string | null;
  /**
   * WP-4 (the multi-user session model, CONTINUOUS_SESSIONS_SPEC_v1 D-1): the
   * row a step of its person on its vendor goes to. Present only while the
   * gateway's model is live for this card's Workspace — its PRESENCE is how
   * the pane knows the card is on the multi-user model (lib/agent/vendor-sessions.ts);
   * absent, everything behaves exactly as before.
   */
  isCurrentVendorSession?: boolean;
  /** WP-4: 'member' (a person's own session) or 'user' (the Workspace owner's agent). */
  principalKind?: string;
  /** WP-4: the person this session runs for, by name (never an e-mail). */
  executorDisplayName?: string | null;
  /** WP-4 (B-10): what really runs the session — claude_tmux, codex_appserver, gemini_acp, ephemeral_legacy, … */
  driver?: string | null;
}

/** WP-4: one of the viewer's own vendor sessions on a card (GET …/sessions/mine). */
export interface OwnVendorSessionSummary {
  id: string;
  sessionNumber: number;
  state: string;
  vendor: Vendor;
  model: string | null;
  effort: string | null;
  modelSource: 'system' | 'role' | 'manual';
  effortSource: 'system' | 'role' | 'manual';
  driver: string | null;
  isCurrentVendorSession: boolean;
  messageCount: number;
  createdAt: string | null;
  lastActivityAt: string | null;
}

/** WP-4 (D-26): what the first step on a vendor will create the row with. */
export interface VendorSeedPreview {
  model: string | null;
  effort: string | null;
  modelSource: 'role' | 'system';
  effortSource: 'role' | 'system';
}

/** WP-4: the viewer's sessions on a card for one vendor. */
export interface OwnVendorEntry {
  vendor: Vendor;
  current: OwnVendorSessionSummary | null;
  /** Live sessions of this vendor that are not current — "Continue in this earlier session?". */
  earlier: OwnVendorSessionSummary[];
  /** No current row but live ones: the session the next step continues. */
  continues: string | null;
  /** No live row at all: the seed the first step will write. */
  preview: VendorSeedPreview | null;
  signIn: { ready: boolean; reason: string | null; canLogIn: boolean; connectUrl: string };
}

export interface MySessionsResponse {
  cardId: string;
  vendors: OwnVendorEntry[];
}

export interface EnsureMySessionResponse {
  session: OwnVendorSessionSummary | null;
  created: boolean;
  promoted: boolean;
  seed?: {
    fromSessionId: string | null;
    rolesCopied: Array<{ roleId: string; name: string | null }>;
    rolesSkipped: Array<{ roleId: string; name: string | null; reason: string }>;
    failed?: boolean;
  };
}

export interface MakeCurrentResponse {
  ok: true;
  current: string;
  previous: string | null;
  alreadyCurrent?: boolean;
}

/** WP-4: one entry of the card timeline (who · vendor · model · effort · outcome). */
export interface SessionTimelineEntry {
  id: string;
  at: string;
  sessionId: string;
  sessionNumber: number;
  kind: 'step' | 'reply' | 'event';
  vendor: Vendor | null;
  authorUserId: string | null;
  authorName: string | null;
  executorUserId: string | null;
  executorName: string | null;
  model: string | null;
  effort: string | null;
  outcome: string | null;
  /** D-14: the executor's answer to the agent's question. */
  askAnswer: boolean;
  bodyPreview: string;
}

export interface CardEventProjection {
  v: 1;
  kind: 'card_event';
  id: string;
  cardId: string;
  at: string;
  event: 'card_moved' | 'note_added' | 'timeline_write_refused';
  actorName: string | null;
  summary: string;
}
export type CardTimelineEntry = SessionTimelineEntry | CardEventProjection;

export interface StartSessionOpts {
  mode?: V6SessionMode;
  /**
   * WP-4a own-session start (batch-I window 2): return the caller's own live
   * chat session on this card, for this vendor, if one exists when the start
   * is serialized on the gateway, instead of starting a second one. Sent only
   * when true; omitted, the request body is unchanged.
   */
  reuseOwnLive?: boolean;
}

interface SessionsResponse {
  sessions: V6Session[];
}
interface SessionResponse {
  session: V6Session;
}

export const sessionsApi = {
  /**
   * Would the terminal socket for this session be admitted right now? 200
   * `{ ok: true }` or the refusal the upgrade would have made, with the
   * gateway's own sentence in the JSON body (`message`). A browser cannot read
   * a failed WebSocket handshake, so this is how the terminal learns WHY it was
   * refused (e.g. 403 `terminal_executor_only`). Asked only after a connection
   * failed; it attaches nothing.
   */
  terminalAccess(sessionId: string): Promise<{ ok: true }> {
    return apiJson<{ ok: true }>(`/sessions/${sessionId}/terminal-access`);
  },
  listForCard(cardId: string): Promise<V6Session[]> {
    return apiJson<SessionsResponse>(`/cards/${cardId}/sessions`).then((r) => r.sessions);
  },
  get(sessionId: string): Promise<V6Session> {
    return apiJson<SessionResponse>(`/sessions/${sessionId}`).then((r) => r.session);
  },
  start(
    cardId: string,
    agent: AgentType,
    principalFence: string,
    opts?: StartSessionOpts,
  ): Promise<V6Session> {
    return apiJson<SessionResponse>(`/cards/${cardId}/sessions/start`, {
      method: 'POST',
      json: opts?.reuseOwnLive
        ? { agent, mode: opts?.mode ?? 'cli', reuseOwnLive: true }
        : { agent, mode: opts?.mode ?? 'cli' },
      headers: { 'X-Chaos-Expected-Principal': principalFence },
    }).then((r) => r.session);
  },
  sleep(sessionId: string, principalFence: string): Promise<V6Session> {
    return apiJson<SessionResponse>(`/sessions/${sessionId}/sleep`, {
      method: 'POST',
      json: {},
      headers: { 'X-Chaos-Expected-Principal': principalFence },
    }).then((r) => r.session);
  },
  resume(sessionId: string, principalFence: string): Promise<V6Session> {
    return apiJson<SessionResponse>(`/sessions/${sessionId}/resume`, {
      method: 'POST',
      json: {},
      headers: { 'X-Chaos-Expected-Principal': principalFence },
    }).then((r) => r.session);
  },
  destroy(sessionId: string, principalFence: string): Promise<void> {
    return apiJson<void>(`/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: { 'X-Chaos-Expected-Principal': principalFence },
    });
  },
  /**
   * WP-4: the viewer's own vendor sessions on the card, per vendor — the
   * current row, the earlier ones, the preview of a row that does not exist
   * yet, sign-in readiness. Asked only once the card's session list says the
   * multi-user model is live (a 404 with the app's not-found body otherwise).
   */
  mine(cardId: string): Promise<MySessionsResponse> {
    return apiJson<MySessionsResponse>(`/cards/${cardId}/sessions/mine`);
  },
  /**
   * WP-4: resolve or create the viewer's own current session for one vendor —
   * the first step on a vendor without a row. A pick made before it (model,
   * effort) lands on the new row as the person's own ('manual', D-26).
   */
  ensureMine(
    cardId: string,
    body: { vendor: Vendor; model?: string; effort?: string },
    principalFence: string,
  ): Promise<EnsureMySessionResponse> {
    return apiJson<EnsureMySessionResponse>(`/cards/${cardId}/sessions/mine`, {
      method: 'POST',
      json: body,
      headers: { 'X-Chaos-Expected-Principal': principalFence },
    });
  },
  /** WP-4: "Continue in this earlier session" — make one of the viewer's own sessions current again. */
  makeCurrent(sessionId: string, principalFence: string): Promise<MakeCurrentResponse> {
    return apiJson<MakeCurrentResponse>(`/sessions/${sessionId}/make-current`, {
      method: 'POST',
      json: {},
      headers: { 'X-Chaos-Expected-Principal': principalFence },
    });
  },
  /** WP-4: the card timeline — every session's steps, labelled (newest `limit`, oldest first). */
  timeline(cardId: string, opts: { since?: string; limit?: number } = {}): Promise<CardTimelineEntry[]> {
    const q = new URLSearchParams();
    if (opts.since) q.set('since', opts.since);
    if (opts.limit) q.set('limit', String(opts.limit));
    const qs = q.toString();
    return apiJson<{ cardId: string; entries: CardTimelineEntry[] }>(
      `/cards/${cardId}/timeline${qs ? `?${qs}` : ''}`,
    ).then((r) => r.entries);
  },
  /**
   * M-X8 (2026-05-22): partial-update a session.
   *
   * M-X16 (2026-05-22): extended to accept model + effort so the chat
   * composer picker persists per-session and a role's defaults survive
   * a reload. Keys omitted from the patch object are left alone; `null`
   * clears the value.
   *
   * M-X26 (2026-05-22): adds subagentPreference (Auto/Orchestrate/Direct).
   * Unlike vendor/model/effort, this column is NOT NULL on the DB row,
   * so `null` is rejected by the gateway with `invalid_subagent_preference`.
   *
   * 2026-05-25 (founder spec): adds fastMode (Claude CLI --fast-mode).
   * Boolean — NOT NULL on the DB row.
   */
  update(
    sessionId: string,
    patch: {
      vendor?: Vendor | null;
      model?: string | null;
      effort?: Effort | null;
      subagentPreference?: SubagentPreference;
      fastMode?: boolean;
    },
    principalFence: string,
  ): Promise<V6Session> {
    return apiJson<SessionResponse>(`/sessions/${sessionId}`, {
      method: 'PATCH',
      json: patch,
      headers: { 'X-Chaos-Expected-Principal': principalFence },
    }).then((r) => r.session);
  },
};

export const sessionsKeys = {
  all: ['v6-sessions'] as const,
  forCard: (cardId: string) => [...sessionsKeys.all, 'card', cardId] as const,
  byId: (sessionId: string) => [...sessionsKeys.all, 'id', sessionId] as const,
  /** WP-4: the viewer's own vendor sessions on a card. */
  mine: (cardId: string) => [...sessionsKeys.all, 'mine', cardId] as const,
  /** WP-4: the card timeline. */
  timeline: (cardId: string) => [...sessionsKeys.all, 'timeline', cardId] as const,
};
